



























import { Component, Ref, Vue } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import { MiraExceptionSuggestion } from "@/data/models/MiraExceptionAdministration";
import { dismissMiraExceptionSuggestion } from "@/data/repository/MiraExceptionAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { canDismissWithReason, MAX_REASON_LENGTH, suggestionErrorMessage } from "@/utils/MiraExceptionAdministration";

/** What `show()` resolves with: dismissed, refused because the suggestion is gone or changed (the caller reloads), or closed. */
export type MiraSuggestionDismissResult = "DISMISSED" | "RELOAD" | null;

/**
 * VERWERFEN for one of Mira's Mira-Ausnahmen suggestions (R8 Phase 3): asks why, then hides the suggestion
 * for good, unless clearly more late recordings turn up there later. IRES Cloud records who dismissed it,
 * when and why (`dismissMiraExceptionSuggestionForAdministration`).
 */
@Component({ components: { "base-dialog": BaseDialogComponent } })
export default class MiraExceptionSuggestionDismissDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  busy = false;
  reason = "";
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  suggestion: MiraExceptionSuggestion | null = null;
  summary = "";

  readonly maxReasonLength = MAX_REASON_LENGTH;

  private resolve: ((result: MiraSuggestionDismissResult) => void) | null = null;

  get canDismiss(): boolean {
    return canDismissWithReason(this.reason);
  }

  /**
   * @param suggestion - the suggestion to dismiss.
   * @param summary - what it is, as the list shows it ("Klünder · Projektabschluss · 07.05.2024").
   */
  show(suggestion: MiraExceptionSuggestion, summary: string): Promise<MiraSuggestionDismissResult> {
    this.suggestion = suggestion;
    this.summary = summary;
    this.reason = "";
    this.baseDialog.show({
      title: this.$t("admin.miraExceptions.suggestions.dismiss.title").toString(),
      positiveButtonText: this.$t("admin.miraExceptions.suggestions.dismiss.action").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      persistent: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  async onDismiss() {
    if (!this.suggestion || !this.canDismiss) return;
    this.busy = true;
    try {
      await dismissMiraExceptionSuggestion({ id: this.suggestion.id, reason: this.reason.trim(), expectedUpdatedAt: this.suggestion.updatedAt });
      SnackbarModule.showSuccess({ key: "admin.miraExceptions.suggestions.dismissed" });
      this.close("DISMISSED");
    } catch (error) {
      const message = suggestionErrorMessage(error);
      SnackbarModule.showError(message);
      // Gone or changed overnight: close, so the list reloads and shows what is there now.
      if (message.key.endsWith("NO_SUGGESTION") || message.key.endsWith("CHANGED_MEANWHILE")) {
        this.close("RELOAD");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraSuggestionDismissResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  finish(result: MiraSuggestionDismissResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
